<script setup lang="ts">
import { PropType } from 'vue'
import { utcToZonedTime } from 'date-fns-tz'

/* Components */
import AppButton from '@/components/App/AppButton.vue'
import IconSeoDropDown from '@/components/Vuetify/IconSeoDropDown.vue'

/* Stores */
import { useCityStore } from '@/store/city/city'
import { useAppStore } from '@/store/app/app'

/* Utils */
import { copyToClipboard } from '@/utils/copyToClipboard'

/* Types */
import {
  ModifiedWorkingHours,
  Tag,
  WorkingHours,
} from '@/api/modules/delivery/types'
import { SnackbarType } from '@/store/app/app.types'
import { PointProperties } from '@/api/modules/delivery/types'

const props = defineProps({
  point: {
    type: Object as PropType<PointProperties>,
    required: true,
  },
  isFirstPoint: {
    type: Boolean,
    required: true,
  },
  selectedPointId: {
    type: Number as PropType<Number | null>,
    required: true,
  },
})

const router = useRouter()

const cityStore = useCityStore()
const { showSnackbar } = useAppStore()

const icons = {
  cafe: {
    icon: '/delivery-food-court.svg',
    alt: 'Кафе',
  },
  card: {
    icon: '/delivery-credit-card.svg',
    alt: 'Карта',
  },
}

const currentWorkingDay = ref('')

const isPointOpen = computed(() =>
  props.point.tags.some((tag: Tag) => tag.slug === 'is_open'),
)

const makeDistanceFormat = (distance: number): string => {
  const d = Number(distance.toFixed(0))

  if (d < 1000) return `~${d} м`
  if (d / 1000 >= 10) return `~ ${(d / 1000).toFixed(0)} км`
  return `&nbsp;~&nbsp;${(d / 1000).toFixed(1)}&nbsp;км`
}

const displayedTags = computed(() => {
  const iconsKeys = Object.keys(icons)

  return props.point.tags
    .filter((tag) => iconsKeys.includes(tag.slug))
    .slice()
    .reverse()
})

const modifiedWorkingHoursHelper = (
  workingHours: WorkingHours[],
  weekday: number,
): ModifiedWorkingHours[] => {
  const weekdays = ['пн', 'вт', 'ср', 'чт', 'пт', 'сб', 'вс']

  const modifyWorkingHour = (item: WorkingHours): ModifiedWorkingHours => {
    // запись вида 'ПН-СР' => [0,2]
    if (item.label.includes('-')) {
      const [start, end] = item.label
        .toLowerCase()
        .split('-')
        .map((a) => weekdays.indexOf(a))

      const isCurrent = weekday >= start && weekday <= end

      return { ...item, isCurrent }
    }

    // запись вида 'ПТ' => 4
    const isCurrent = weekdays.indexOf(item.label) === weekday

    return { ...item, isCurrent }
  }

  return workingHours.map(modifyWorkingHour)
}

const workingHoursTitle = computed(
  () =>
    `${isPointOpen.value ? 'Открыта' : 'Закрыта'} ${currentWorkingDay.value}`,
)

const modifiedWorkingHours = computed(() => {
  const currentTime = cityStore.cityServerTimeISO
  const cityTimezone = cityStore.timezone
  const weekday = utcToZonedTime(currentTime, cityTimezone).getDay() - 1

  return modifiedWorkingHoursHelper(props.point.working_hours, weekday)
})

const renderDistance = computed(() => {
  if (!props.point.distance) return

  return makeDistanceFormat(props.point.distance)
})

onMounted(() => {
  const currentDay = modifiedWorkingHours.value.find((item) => item.isCurrent)

  if (currentDay) {
    currentWorkingDay.value = `${currentDay.label} c ${currentDay.start} до ${currentDay.end}`
  }
})

const copyAddress = () => {
  const successful = copyToClipboard(props.point.address)

  if (successful) {
    showSnackbar({
      message: 'Адрес успешно скопирован',
      type: SnackbarType.SUCCESS,
    })
  }
}
const emits = defineEmits(['openMoreInfo'])

const openMoreInfo = (point: PointProperties | null) => {
  emits('openMoreInfo', point ? point.id : null)

  if (point) router.replace({ query: { slug: point.slug } })
}
</script>

<template>
  <li class="delivery__item">
    <div @click.stop="openMoreInfo(point)">
      <div class="delivery__item-top">
        <span class="delivery__item-title" itemprop="address">
          {{ point.address }}
        </span>

        <div
          v-if="point.distance"
          class="delivery__item-distance"
          :class="{ 'shorter--distance': isFirstPoint }"
        >
          <span v-if="isFirstPoint">Ближайшая</span>
          <span v-html="renderDistance" />
        </div>
      </div>

      <div class="delivery__item-cooktime">
        <span>Время приготовления ~{{ point.cooking_time }} мин.</span>
      </div>

      <div class="delivery__item-bottom">
        <div
          class="delivery__item-hours"
          :class="{ 'point--close': !isPointOpen }"
        >
          {{ workingHoursTitle }}
        </div>
        <div class="delivery__item-tags">
          <div v-for="tag in displayedTags" :key="tag.slug" class="tags__item">
            <nuxt-img
              v-if="icons[tag.slug]"
              class="tags__item-image"
              :alt="icons[tag.slug].alt"
              :src="icons[tag.slug].icon"
            />
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="selectedPointId === props.point.id"
      class="delivery__item-more_info more__info"
    >
      <span class="more__info-headline">Полный режим работы</span>

      <div class="more__info-time">
        <div
          v-for="item in modifiedWorkingHours"
          :key="item.label"
          class="time"
        >
          <div class="time__label">{{ item.label }}</div>
          &nbsp;

          <div v-if="item.is_working">
            <time class="time__hours">
              с&nbsp;{{ item.start }}&nbsp;до&nbsp;{{ item.end }}
            </time>
          </div>

          <div v-else>Выходной</div>
        </div>
      </div>

      <div v-if="point.how_to_find">
        <span class="more__info-headline">Как добраться</span>

        <p class="more__info-description">
          {{ point.how_to_find }}
        </p>
      </div>

      <div class="more__info-buttons">
        <app-button :ripple="false" @click="copyAddress">
          Скопировать адрес
        </app-button>

        <app-button :ripple="false" @click="openMoreInfo(null)">
          Свернуть
          <v-icon
            left
            class="icon"
            :class="{ reverse__image: !!selectedPointId }"
          >
            <icon-seo-drop-down />
          </v-icon>
        </app-button>
      </div>
    </div>
  </li>
</template>

<style lang="scss" scoped>
:deep() {
  .v-icon {
    margin-left: 5px !important;
  }
}
.delivery__item {
  padding: 20px 30px;
  cursor: pointer;
  border-top: 1px solid $button-background;

  &-top,
  &-bottom {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
  }

  &-title {
    max-width: 200px;
    margin-bottom: 10px;
    font-size: 14px;
    font-weight: 800;
    line-height: 17px;
    color: $text-black;
  }

  &-distance {
    font-size: 12px;
    font-weight: 800;
    line-height: 18px;
    color: $primary;
  }

  &-cooktime {
    margin-bottom: 7px;
    font-size: 12px;
    font-weight: 600;
    line-height: 18px;
    color: $text-black;
  }

  &-hours {
    padding: 0;
    font-size: 12px;
    font-weight: 800;
    line-height: 14px;
    color: $success;
    text-transform: uppercase;
  }

  .more__info {
    padding: 20px 30px;
    margin: 20px -30px -20px;
    color: $text-black;
    background: $button-background;
    box-shadow: 0 28px 22px -30px inset rgb(178 183 191);

    &-headline {
      margin-bottom: 10px;
      font-size: 12px;
      font-weight: 800;
      line-height: 17px;
    }

    &-time {
      display: grid;
      grid-template-columns: 1fr 1fr;
      margin-bottom: 20px;
      font-size: 12px;
      font-weight: 600;
      line-height: 22px;

      .time {
        display: flex;
        &__label {
          text-transform: uppercase;
        }
      }
    }

    &-description {
      margin-bottom: 20px;
      font-size: 12px;
      font-weight: 600;
      line-height: 18px;
    }

    &-buttons {
      display: flex;
      justify-content: space-between;
      margin: -10px -10px 0;

      button {
        min-height: max-content;
        font-size: 14px;
        font-weight: 600;
        line-height: 16px;
        color: $primary !important;
        background: none !important;
        border-radius: 10px;
        box-shadow: none !important;
      }
    }
  }

  &-tags {
    display: flex;
    .tags__item {
      position: relative;
      display: flex;
      margin-left: 17px;

      &-image {
        width: 20px;
        height: 20px;
      }
    }
  }

  .shorter--distance {
    height: max-content;
    padding: 1px 10px;
    font-size: 10px;
    font-weight: 800;
    color: $white;
    background-color: $primary;
    border-radius: 100px;
  }

  .point--close {
    color: $unactive-grey;
  }

  &:last-child {
    border-bottom: 1px solid $button-background;
  }
}
.reverse__image {
  transform: rotate(180deg);
}
</style>
